import { format } from "date-fns"
import { RouteStop } from "@/reducers/routes2gSlice"
import { DisplayedStopDistance, formatStopDistance } from "./stopDistance"
import { parseWarsawTime } from "./warsawTime"
import i18n from "@/lib/i18n/i18n"
import { getDateFnsLocale, getIntlLocale } from "@/lib/i18n/format"

/**
 * What the caller already knows about a stop. The map reads these from the
 * fields its list enriched the stops with; the driver overview derives them.
 */
export type StopFacts = { active: boolean; target: boolean; alert: boolean }

export type StopDistance = {
  label: string
  tone: "error" | "positive" | "neutral"
}

/** `arrival` is the backend's string, so each surface formats it its own way. */
export type StopArrival = { arrival: string; secondsLate: number | null }

const DISTANCE_LOCALE = "fr-FR"

export const arrivalTimeOf = (stop: RouteStop): string | undefined =>
  stop.estimated_arrival_time_with_tacho || stop.estimated_arrival_time

const secondsLate = (stop: RouteStop, active: boolean): number | null => {
  if (!stop.estimated_arrival_time && !active) return null
  const eta = parseWarsawTime(arrivalTimeOf(stop))
  const planned = parseWarsawTime(stop.scheduled_arrival_time)
  if (eta === null || planned === null) return null
  return Math.floor((eta - planned) / 1000)
}

const distanceLabelOf = (
  stop: RouteStop,
  displayed: DisplayedStopDistance | undefined,
): string => {
  if (stop.proximity_status === "arrived")
    return i18n.t("maps.stopStatus.arrived")
  if (stop.proximity_status === "arriving")
    return i18n.t("maps.stopStatus.arriving")
  return displayed
    ? formatStopDistance(
        displayed,
        getIntlLocale(DISTANCE_LOCALE) ?? DISTANCE_LOCALE,
      )
    : "?"
}

const distanceToneOf = (
  stop: RouteStop,
  { active, target, alert }: StopFacts,
): StopDistance["tone"] => {
  if (alert) return "error"
  if (target && stop.scheduled_arrival_time) return "positive"
  const late = secondsLate(stop, active)
  return late !== null && late < 0 ? "positive" : "neutral"
}

export const stopDistanceOf = (
  stop: RouteStop,
  displayed: DisplayedStopDistance | undefined,
  facts: StopFacts,
): StopDistance | null => {
  if (stop.action === "passthrough") return null
  if (!facts.active && !stop.is_temporary) return null
  if (
    stop.is_temporary &&
    stop.remaining_distance !== undefined &&
    !stop.proximity_status
  ) {
    if (!stop.remaining_distance || !displayed) return null
    const km = Math.floor(displayed.distanceMeters / 1000)
    return {
      label: `${km.toLocaleString(getIntlLocale(DISTANCE_LOCALE))} km`,
      tone: "neutral",
    }
  }
  if (!stop.proximity_status) return null
  return {
    label: distanceLabelOf(stop, displayed),
    tone: distanceToneOf(stop, facts),
  }
}

/** Why the tacho ETA may be later than the driver could legally manage. */
export const assumedRestLabelOf = (stop: RouteStop): string | undefined => {
  if (!stop.estimated_arrival_time_with_tacho || !stop.assumed_rest_until)
    return undefined
  const until = format(new Date(stop.assumed_rest_until), "HH:mm eee", {
    locale: getDateFnsLocale(),
  })
  return i18n.t("maps.stopStatus.assumedRest", { until })
}

export const stopArrivalOf = (
  stop: RouteStop,
  active: boolean,
): StopArrival | null => {
  const arrival = arrivalTimeOf(stop)
  if (stop.action === "passthrough") return null
  if (!stop.estimated_arrival_time || !arrival || !active) return null
  return { arrival, secondsLate: secondsLate(stop, active) }
}
